// Where the waves go as the sea gets shallow: the beach's wave-phase field, worked out from a window of the ground's heights (the server's shore window, groundClip.ts) whenever a new one comes.
//
// A train of waves slows down as the water shallows (a wave of angular frequency w in depth d has wave number k(d), growing as d falls),
// so its crests bunch up and, because the slower end of a crest lags, turn until they run parallel to the beach (refraction). The phase
// psi(x) of such a train, counted from zero at the waterline and growing seaward, obeys the eikonal equation
//     |grad psi| = k(d(x)),   psi = 0 on the land,
// whose solution is geometric optics for the waves over this bottom: crests are the contours of psi, they travel against grad psi (toward the
// shore) and their local wavelength is 2 pi / |grad psi|. Solved here by fast sweeping (Zhao's method, Godunov upwind update) on the heightfield.
// The renderer (waterView.ts) turns psi into crests that steepen with the shoaling water, break where the wave is as high as 0.78 of the depth,
// leave foam behind them and run up the sand.
//
// psi is computed for one reference frequency (OMEGA_REF) and scaled by w / OMEGA_REF for the sea's actual peak frequency. In shallow water
// k = w / sqrt(g d) so the scaling is exact there; in deeper water it is an approximation, but the waves are only drawn this way in the shallows.
//
// Result: an RGBA32F texture on a grid at most 1024 texels across (every other, or every third, terrain sample on a large world):
//   r: psi (radians at OMEGA_REF), g: d psi / dx, b: d psi / dz (rad/m, three axes: x east, z toward -y), a: ground slope magnitude (m/m).
// Also the sea's offshore depth (the 90th percentile of the depth of the water cells), for the wave spectrum's dispersion.
import * as THREE from 'three';

/** A grid of ground heights (m above the sea; z up in sim, which is three y): sample (i, j) is at x = i * width / (nx - 1), z = -j * height / (ny - 1) from the grid's corner. */
export interface TerrainField { z: Float32Array; nx: number; ny: number; width: number; height: number }

export const OMEGA_REF = 2.5;        // rad/s, T = 2.5 s
const G = 9.81;
const D_MIN_K = 0.15;                // metres: depth below which the wave number stops growing (the bore's length stays a few metres)
const D_FAR = 6.0;                   // metres: deeper than this the wave number is held (the phase there is not used)

/** Wave number of a wave of angular frequency w in depth d (Hunt's approximation to w^2 = g k tanh(k d), good to 0.1%). */
export function hunt(w: number, d: number): number {
  d = Math.max(d, 1e-3);
  const y = w * w * d / G;
  const kd2 = y * y + y / (1 + y * (0.6522 + y * (0.4622 + y * y * y * (0.0864 + y * 0.0675))));
  return Math.sqrt(kd2) / d;
}

export interface ShoreField {
  tex: THREE.DataTexture;
  nx: number; ny: number;          // texture size
  width: number; height: number;   // world size, m (same mapping as the terrain field: x = i * width / (nx-1), z = -j * height / (ny-1))
  stride: number;                  // terrain samples per texel
  depthOff: number;                // offshore depth, m
  psiMax: number;
}

/**
 * Build the phase field. `f`: the terrain heightfield (z up in sim, which is three y). `seaLevel`: still-water level (the mean, without the tide).
 * Takes a few tens of milliseconds for a 640 x 640 world and a fraction of a second for 2000 x 2000.
 */
export function buildShoreField(f: TerrainField, seaLevel: number): ShoreField {
  const stride = Math.max(1, Math.ceil(Math.max(f.nx, f.ny) / 1024));
  const nx = Math.floor((f.nx - 1) / stride) + 1, ny = Math.floor((f.ny - 1) / stride) + 1;
  const hx = f.width / (f.nx - 1) * stride, hy = f.height / (f.ny - 1) * stride;
  const n = nx * ny;
  const depth = new Float32Array(n);
  const speed = new Float32Array(n);     // k(d)
  const psi = new Float32Array(n);
  const INF = 1e9;
  const wet: number[] = [];
  for (let j = 0; j < ny; j++) for (let i = 0; i < nx; i++) {
    const z = f.z[(j * stride) * f.nx + i * stride];
    const d = seaLevel - z;
    const k = n3(d);
    depth[j * nx + i] = d;
    speed[j * nx + i] = k;
    psi[j * nx + i] = d <= 0 ? 0 : INF;
    if (d > 0 && ((i + j * 7) % 11 === 0)) wet.push(d);
  }
  function n3(d: number): number { return d <= 0 ? 0 : hunt(OMEGA_REF, Math.min(Math.max(d, D_MIN_K), D_FAR)); }

  // Fast sweeping: four diagonal orderings, repeated until nothing changes by more than a milliradian (a handful of rounds).
  const sweep = (i0: number, i1: number, di: number, j0: number, j1: number, dj: number): number => {
    let changed = 0;
    for (let j = j0; j !== j1; j += dj) {
      for (let i = i0; i !== i1; i += di) {
        const idx = j * nx + i;
        if (depth[idx] <= 0) continue;
        const a = Math.min(i > 0 ? psi[idx - 1] : INF, i < nx - 1 ? psi[idx + 1] : INF);
        const b = Math.min(j > 0 ? psi[idx - nx] : INF, j < ny - 1 ? psi[idx + nx] : INF);
        const k = speed[idx];
        let nu: number;
        // |grad psi| = k on a grid with spacings hx, hy: solve ((nu - a)/hx)^2 + ((nu - b)/hy)^2 = k^2 (upwind: only neighbours below nu count)
        const ta = a + k * hx, tb = b + k * hy;
        if (a >= INF && b >= INF) continue;
        if (ta <= b) nu = ta;
        else if (tb <= a) nu = tb;
        else {
          const A = 1 / (hx * hx) + 1 / (hy * hy), B = -2 * (a / (hx * hx) + b / (hy * hy)), C = a * a / (hx * hx) + b * b / (hy * hy) - k * k;
          const disc = B * B - 4 * A * C;
          nu = disc >= 0 ? (-B + Math.sqrt(disc)) / (2 * A) : Math.min(ta, tb);
          nu = Math.min(nu, ta, tb);
        }
        if (nu < psi[idx] - 1e-3) { psi[idx] = nu; changed++; }
      }
    }
    return changed;
  };
  for (let round = 0; round < 12; round++) {
    let c = 0;
    c += sweep(0, nx, 1, 0, ny, 1);
    c += sweep(nx - 1, -1, -1, 0, ny, 1);
    c += sweep(nx - 1, -1, -1, ny - 1, -1, -1);
    c += sweep(0, nx, 1, ny - 1, -1, -1);
    if (c === 0) break;
  }
  let psiMax = 0;
  for (let i = 0; i < n; i++) { if (psi[i] >= INF) psi[i] = 0; else if (psi[i] > psiMax) psiMax = psi[i]; }

  // gradient (one-sided at the waterline, where the land's zero would halve it) and the ground's slope
  const data = new Float32Array(n * 4);
  for (let j = 0; j < ny; j++) for (let i = 0; i < nx; i++) {
    const idx = j * nx + i;
    const il = Math.max(0, i - 1), ir = Math.min(nx - 1, i + 1), jl = Math.max(0, j - 1), jr = Math.min(ny - 1, j + 1);
    let gx: number, gy: number;
    if (depth[idx] > 0) {
      // use only wet neighbours (or itself) so that the zero on land does not enter
      const wl = depth[j * nx + il] > 0 ? il : i, wr = depth[j * nx + ir] > 0 ? ir : i;
      const wd = depth[jl * nx + i] > 0 ? jl : j, wu = depth[jr * nx + i] > 0 ? jr : j;
      gx = wr !== wl ? (psi[j * nx + wr] - psi[j * nx + wl]) / ((wr - wl) * hx) : 0;
      gy = wu !== wd ? (psi[wu * nx + i] - psi[wd * nx + i]) / ((wu - wd) * hy) : 0;
    } else { gx = 0; gy = 0; }
    // texture rows are sim y = j * hy; three z = -y, so d/dz = -d/dy
    data[idx * 4] = psi[idx];
    data[idx * 4 + 1] = gx;
    data[idx * 4 + 2] = -gy;
    const zx = (depth[j * nx + il] - depth[j * nx + ir]) / ((ir - il || 1) * hx), zy = (depth[jl * nx + i] - depth[jr * nx + i]) / ((jr - jl || 1) * hy);
    data[idx * 4 + 3] = Math.hypot(zx, zy);       // ground slope: d(ground)/dx = -d(depth)/dx
  }
  const tex = new THREE.DataTexture(data, nx, ny, THREE.RGBAFormat, THREE.FloatType);
  tex.minFilter = tex.magFilter = THREE.NearestFilter; tex.generateMipmaps = false; tex.needsUpdate = true;

  wet.sort((a, b) => a - b);
  const depthOff = wet.length ? wet[Math.floor(wet.length * 0.9)] : 3;
  return { tex, nx, ny, width: f.width, height: f.height, stride, depthOff, psiMax };
}
